import React, { useEffect } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';

import { faBold, faHeading, faItalic, faListOl, faListUl, faStrikethrough, faUnderline } from '~components/icon';

import MergeTokenHighlight from './mergeTokenHighlight';
import RichTextToolbarButton from './RichTextToolbarButton';

interface ComponentProps {
  value: string; // HTML
  onChange: (html: string) => void;
  ariaLabel?: string;
}

// StarterKit brings bold, italic, strike, headings, bullet/ordered lists, history. Underline is the one
// core mark it omits, so it's added explicitly. MergeTokenHighlight chips pasted {{merge:}} sentinels.
// No tables/images/links — intentionally out of scope.
const EXTENSIONS = [StarterKit, Underline, MergeTokenHighlight];

/**
 * Tiptap (ProseMirror) authoring surface for a rich_text block. Emits semantic HTML on every change,
 * which is what gets persisted and sanitised for the read-only filler view (RichTextView).
 */
const RichTextEditor: React.FC<ComponentProps> = ({ value, onChange, ariaLabel }) => {
  const editor = useEditor({
    extensions: EXTENSIONS,
    content: value || '',
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
    editorProps: {
      attributes: {
        class: 'rich-text-editable',
        'aria-label': ariaLabel ?? 'Rich text content',
        role: 'textbox',
        'aria-multiline': 'true',
      },
    },
  });

  // Keep the editor in sync if `value` is replaced from outside (e.g. a different field is loaded into
  // this instance). The guard prevents a feedback loop: after onUpdate, getHTML() already equals value.
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className="rich-text-editor rich-text-editor--tiptap" data-cy="rich-text-editor-tiptap">
      <div className="rich-text-toolbar" role="toolbar" aria-label="Text formatting">
        <RichTextToolbarButton
          icon={faBold}
          label="Bold"
          isActive={editor.isActive('bold')}
          onActivate={() => editor.chain().focus().toggleBold().run()}
        />
        <RichTextToolbarButton
          icon={faItalic}
          label="Italic"
          isActive={editor.isActive('italic')}
          onActivate={() => editor.chain().focus().toggleItalic().run()}
        />
        <RichTextToolbarButton
          icon={faUnderline}
          label="Underline"
          isActive={editor.isActive('underline')}
          onActivate={() => editor.chain().focus().toggleUnderline().run()}
        />
        <RichTextToolbarButton
          icon={faStrikethrough}
          label="Strikethrough"
          isActive={editor.isActive('strike')}
          onActivate={() => editor.chain().focus().toggleStrike().run()}
        />
        <span className="rich-text-toolbar-divider" aria-hidden="true" />
        <RichTextToolbarButton
          icon={faHeading}
          label="Heading (large)"
          isActive={editor.isActive('heading', { level: 1 })}
          onActivate={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        />
        <RichTextToolbarButton
          icon={faHeading}
          label="Heading (small)"
          isActive={editor.isActive('heading', { level: 2 })}
          onActivate={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        />
        <span className="rich-text-toolbar-divider" aria-hidden="true" />
        <RichTextToolbarButton
          icon={faListUl}
          label="Bullet list"
          isActive={editor.isActive('bulletList')}
          onActivate={() => editor.chain().focus().toggleBulletList().run()}
        />
        <RichTextToolbarButton
          icon={faListOl}
          label="Numbered list"
          isActive={editor.isActive('orderedList')}
          onActivate={() => editor.chain().focus().toggleOrderedList().run()}
        />
      </div>
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextEditor;
